import {
  APIComponentAttributes,
  APIComponentType,
  APIFieldAttributes,
  APIModelAttributes,
  ArdoqId,
  TypeId,
} from '@ardoq/api-types';
import { getValidTypeByParent } from './modelOperations';
import { DIFF_TYPE, LI_BACKGROUND_CLASS_NAME } from '@ardoq/global-consts';
import {
  ComponentTypesById,
  DiffType,
  GetCssClassNamesOption,
  ModelEntityTypePair,
  RepresentationData,
} from '@ardoq/data-model';
import { getIcon } from '@ardoq/icons';

type FormatFieldValue = (args: {
  fieldId: ArdoqId;
  value: unknown;
  excludeTime: boolean;
}) => string | null;

type GetRawLabelArgs = {
  component: APIComponentAttributes;
  componentType: APIComponentType | null;
  labelKey: string;
  excludeTime: boolean;
  fields: APIFieldAttributes[];
  formatFieldValue: FormatFieldValue;
};

export const getTypeId = (component: APIComponentAttributes): TypeId => {
  return component.typeId;
};

export const getEntityTypePair = (
  component: APIComponentAttributes
): ModelEntityTypePair => {
  return {
    typeId: getTypeId(component),
    modelId: component.model,
  };
};

export const getVisualDiffType = (
  component: APIComponentAttributes
): DiffType => component[DIFF_TYPE] || DiffType.NONE;

export const hasURLFields = (
  component: APIComponentAttributes,
  urlFields: APIFieldAttributes[]
): boolean =>
  urlFields.some(field =>
    Boolean((component as Record<string, unknown>)[field.name])
  );

export const getCssClassNames = (
  component: APIComponentAttributes,
  componentType: APIComponentType | null,
  sanitizedFilterColor: string | undefined,
  { useAsBackgroundStyle = false }: Partial<GetCssClassNamesOption> = {}
): string =>
  [
    'component',
    `id_${component._id}`,
    componentType?.id,
    sanitizedFilterColor,
    useAsBackgroundStyle && LI_BACKGROUND_CLASS_NAME,
  ]
    .filter(Boolean)
    .join(' ');

export const getColor = (
  component: APIComponentAttributes,
  componentType: APIComponentType | null
): string | null => {
  if (component.color) return component.color;
  return componentType?.color || null;
};

export const getShape = (
  component: APIComponentAttributes,
  componentType: APIComponentType | null
): string => {
  if (component.shape) return component.shape;
  return componentType?.shape ?? 'process';
};

export const getImage = (
  component: APIComponentAttributes,
  componentType: APIComponentType | null
): string | null => {
  if (component.image) return component.image;
  return componentType?.image ?? null;
};

export const getRawLabel = ({
  component,
  componentType,
  labelKey,
  excludeTime,
  fields,
  formatFieldValue,
}: GetRawLabelArgs): string | null => {
  if (labelKey === 'none') return '';
  if (labelKey === 'type') return componentType?.name ?? null;

  const componentName = component.name || '';
  if (labelKey === 'name') return componentName;

  const fieldValue = (component as Record<string, unknown>)[labelKey];
  const field = fields.find(f => f.name === labelKey);

  const formattedValue = field
    ? formatFieldValue({ fieldId: field._id, value: fieldValue, excludeTime })
    : fieldValue;

  const hasFormattedValue = formattedValue || formattedValue === 0;
  const labelSuffix = hasFormattedValue ? `\n[${formattedValue}]` : '';
  return `${componentName}${labelSuffix}`;
};

type ComponentTypeRepresentationSource = {
  image: string | null;
  icon: string | null;
};

export const getRepresentationData = (
  component: APIComponentAttributes,
  componentType: ComponentTypeRepresentationSource | null
): RepresentationData => {
  if (component.image) {
    return {
      isImage: true,
      value: component.image,
      icon: getIcon(component.image),
    };
  }
  if (component.icon) {
    return {
      isImage: false,
      value: component.icon,
      icon: getIcon(component.icon),
    };
  }
  if (componentType) {
    const value = componentType.image || componentType.icon;
    return {
      isImage: Boolean(componentType.image),
      value,
      icon: getIcon(value),
    };
  }
  return { isImage: false, value: null, icon: getIcon(null) };
};

export const isComponentWithParent = (
  component: APIComponentAttributes
): component is APIComponentAttributes & { parent: string } =>
  component.parent !== null;

const hasChildren = (
  componentTypesById: ComponentTypesById,
  typeId: ArdoqId
): boolean => {
  const type = componentTypesById[typeId];
  if (!type) return false;
  return Object.keys(type.children).length > 0;
};

export const canComponentHaveChildren = (
  component: APIComponentAttributes,
  componentTypesById: ComponentTypesById,
  isFlexible: boolean
): boolean => isFlexible || hasChildren(componentTypesById, component.typeId);

export const isValidChildType = (
  componentTypesById: ComponentTypesById,
  parentTypeId: ArdoqId,
  childTypeId: ArdoqId
): boolean => {
  const parentType = componentTypesById[parentTypeId];
  if (!parentType) return false;
  return Object.prototype.hasOwnProperty.call(parentType.children, childTypeId);
};

export const getPossibleParents = (
  component: APIComponentAttributes,
  workspaceComponents: APIComponentAttributes[],
  descendantIds: ArdoqId[],
  isFlexible: boolean,
  componentTypesById: ComponentTypesById
): APIComponentAttributes[] => {
  const descendantSet = new Set(descendantIds);
  const hasDescendants = descendantIds.length > 0;

  return workspaceComponents.filter(candidate => {
    if (candidate._id === component._id) return false;
    if (descendantSet.has(candidate._id)) return false;
    if (isFlexible) return true;

    if (!hasDescendants) {
      return hasChildren(componentTypesById, candidate.typeId);
    }

    return isValidChildType(
      componentTypesById,
      candidate.typeId,
      component.typeId
    );
  });
};

export const isRootComponent = (
  workspaceId: ArdoqId,
  component: APIComponentAttributes
): boolean => !component.parent && component.rootWorkspace === workspaceId;

export const getRootComponents = (
  workspaceId: ArdoqId,
  components: APIComponentAttributes[]
): APIComponentAttributes[] =>
  components.filter(component => isRootComponent(workspaceId, component));

export const buildChildrenByParent = (
  components: APIComponentAttributes[]
): Map<ArdoqId, APIComponentAttributes[]> => {
  const childrenByParent = new Map<ArdoqId, APIComponentAttributes[]>();
  for (const component of components) {
    if (!component.parent) continue;
    const siblings = childrenByParent.get(component.parent);
    if (siblings) {
      siblings.push(component);
    } else {
      childrenByParent.set(component.parent, [component]);
    }
  }
  return childrenByParent;
};

export const getFlatHierarchy = (
  roots: APIComponentAttributes[],
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>
): APIComponentAttributes[] => {
  const collect = (
    component: APIComponentAttributes,
    into: APIComponentAttributes[]
  ): void => {
    const children = childrenByParent.get(component._id);
    if (!children) return;
    for (const child of children) {
      into.push(child);
      collect(child, into);
    }
  };

  const flat: APIComponentAttributes[] = [];
  for (const root of roots) {
    flat.push(root);
    collect(root, flat);
  }
  return flat;
};

export const getFullPathName = (
  componentsById: Record<ArdoqId, APIComponentAttributes>,
  componentId: ArdoqId
): string | null => {
  const component = componentsById[componentId];
  if (!component) return null;
  const name = component.name || '';
  const parentPath = component.parent
    ? getFullPathName(componentsById, component.parent)
    : null;
  return parentPath ? `${parentPath} / ${name}` : name;
};

export const buildParentChangeCascade = (
  updated: APIComponentAttributes,
  byId: Record<ArdoqId, APIComponentAttributes>,
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>,
  modelData: APIModelAttributes
): APIComponentAttributes[] => {
  const current = byId[updated._id];
  if (!current || current.parent === updated.parent) return [updated];

  if (modelData.flexible && updated.typeId) return [updated];

  const newParentTypeId = updated.parent
    ? (byId[updated.parent]?.typeId ?? null)
    : null;

  const newType = getValidTypeByParent(
    modelData,
    updated.typeId,
    newParentTypeId
  );
  if (!newType || newType.id === updated.typeId) return [updated];

  const result: APIComponentAttributes[] = [
    { ...updated, typeId: newType.id, type: newType.name },
  ];

  const walk = (parentId: ArdoqId, parentTypeId: string): void => {
    const children = childrenByParent.get(parentId);
    if (!children) return;
    for (const child of children) {
      const childNewType = getValidTypeByParent(
        modelData,
        child.typeId,
        parentTypeId
      );
      if (!childNewType || childNewType.id === child.typeId) continue;
      result.push({
        ...child,
        typeId: childNewType.id,
        type: childNewType.name,
      });
      walk(child._id, childNewType.id);
    }
  };

  walk(updated._id, newType.id);
  return result;
};
